import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChatBubbleLeftRightIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useAuthFetch } from "../../hooks/useAuthFetch";
import { useMessageComposer } from "../../contexts/MessageComposerContext";
import { Table } from "../../components/table";
import { Button } from "../../components/button";
import { PageHeading } from "../../components/pageHeading";
import { DeleteConfirm } from "../../components/modal/deleteConfirm";
import { MultiSelect } from "../../components/menu/multiSelect";
import Datepicker from "react-tailwindcss-datepicker";
import classNames from "../../utils/classNames";
import { API, MESSAGE_TYPES, formatDateTime } from "./messageUtils";

const TYPE_FILTERS = [
  { id: "all", name: "All" },
  ...Object.entries(MESSAGE_TYPES).map(([id, t]) => ({ id, name: t.label })),
];

// Messages the office has sent to guards, newest first. Rows open the shared
// message sheet.

// The picker gives "YYYY-MM-DD" strings or Dates; either way, the local day.
const dayString = (value) => {
  if (!value) return null;
  if (typeof value === "string") return value.slice(0, 10);
  const pad = (n) => String(n).padStart(2, "0");
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
};

function Messaging() {
  const authFetch = useAuthFetch();
  const { openComposer, openMessage, savedAt } = useMessageComposer();
  const [type, setType] = useState("all");
  // Empty = all dates. Same range picker (with shortcuts) as Activities.
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
  const fromDay = dayString(dateRange.startDate);
  const toDay = dayString(dateRange.endDate);
  const [messages, setMessages] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const fetchMessages = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (type !== "all") params.set("type", type);
      // Whole local days: from the start of the first day to the start of
      // the day after the last.
      if (fromDay) params.set("since", new Date(`${fromDay}T00:00`).toISOString());
      if (toDay) {
        const end = new Date(`${toDay}T00:00`);
        end.setDate(end.getDate() + 1);
        params.set("until", end.toISOString());
      }
      const response = await authFetch(`${API}/v1/messages?${params}`);
      if (response.ok) setMessages(await response.json());
      else console.error("Error fetching messages");
    } catch (error) {
      console.error("Error fetching messages", error);
    } finally {
      setIsLoading(false);
    }
  }, [type, fromDay, toDay, authFetch]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages, savedAt]);

  // Deep link: /messaging?open=<id> opens that message.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId) {
      openMessage(Number(openId));
      searchParams.delete("open");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, openMessage]);

  const deleteMessage = async (id) => {
    const response = await authFetch(`${API}/v1/messages/delete/${id}`, { method: "PUT" });
    if (!response.ok) throw new Error("Failed to delete");
  };

  const columns = useMemo(
    () => [
      {
        Header: "Message",
        accessor: "title",
        Cell: ({ row }) => {
          const def = MESSAGE_TYPES[row.original.type] || MESSAGE_TYPES.general;
          const Icon = def.icon;
          return (
            <button
              type="button"
              onClick={() => openMessage(row.original.id)}
              className="flex items-center gap-2 text-left text-gray-800 dark:text-gray-100 hover:text-gray-900 dark:hover:text-white hover:underline"
            >
              <Icon
                className={classNames(
                  "h-4 w-4 shrink-0",
                  row.original.type === "bolo" ? "text-danger" : "text-gray-400"
                )}
              />
              {row.original.title}
            </button>
          );
        },
      },
      {
        Header: "Type",
        id: "type",
        accessor: (m) => MESSAGE_TYPES[m.type]?.label || m.type,
      },
      {
        Header: "Sent to",
        id: "audience",
        accessor: (m) => (m.audience === "all" ? "Everyone clocked in" : "Clocked in at sites"),
      },
      {
        Header: "Read",
        id: "read",
        accessor: (m) => m.read_count / (m.recipient_count || 1),
        Cell: ({ row }) => (
          <button
            type="button"
            onClick={() => openMessage(row.original.id, { tab: "Recipients" })}
            className="text-gray-700 dark:text-gray-200 hover:underline"
          >
            {row.original.read_count}/{row.original.recipient_count}
          </button>
        ),
      },
      {
        Header: "Confirmed",
        id: "confirmed",
        accessor: (m) => (m.requires_ack ? m.acknowledged_count / (m.recipient_count || 1) : -1),
        Cell: ({ row }) =>
          row.original.requires_ack ? (
            <button
              type="button"
              onClick={() => openMessage(row.original.id, { tab: "Recipients" })}
              className="text-gray-700 dark:text-gray-200 hover:underline"
            >
              {row.original.acknowledged_count}/{row.original.recipient_count}
            </button>
          ) : (
            <span className="text-gray-300 dark:text-gray-600">Not required</span>
          ),
      },
      {
        Header: "Sent",
        id: "created_at",
        // ISO timestamps sort chronologically as strings.
        accessor: "created_at",
        Cell: ({ row }) => formatDateTime(row.original.created_at),
      },
      {
        Header: "Sent by",
        id: "created_by_name",
        accessor: (m) => m.created_by_name || "",
      },
      {
        Header: "",
        id: "delete",
        accessor: () => "delete",
        Cell: ({ row }) => (
          <button
            onClick={() => setDeleteItem({ identifier: row.original.title, id: row.original.id })}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors duration-150"
          >
            <TrashIcon className="h-4 w-4 shrink-0" />
          </button>
        ),
      },
    ],
    [openMessage]
  );

  return (
    <div>
      <main className="py-6">
        <div className="px-4 sm:px-6 lg:px-6">
          <PageHeading title="Messaging">
            <Button icon={PlusIcon} onClick={() => openComposer()}>
              New message
            </Button>
          </PageHeading>

          <div className="mb-4 ml-1 flex flex-wrap items-center gap-3">
            <MultiSelect
              label="Type"
              options={TYPE_FILTERS}
              selected={[type]}
              onUpdate={([value]) => setType(value)}
              singleSelect
            />
            <div className="w-fit">
              <Datepicker
                startFrom={new Date("2024-01-05")}
                showShortcuts={true}
                inputClassName={
                  "h-9 w-64 inline-flex rounded-md px-3 items-center text-sm leading-none tracking-normal font-normal text-gray-600 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/[0.06] bg-gray-50 dark:bg-gray-800 border-0 pr-10 focus:outline-none"
                }
                separator={"to"}
                placeholder="All dates"
                value={dateRange}
                onChange={(value) => setDateRange(value || { startDate: null, endDate: null })}
              />
            </div>
          </div>

          <Table
            data={messages || []}
            columns={columns}
            isLoading={isLoading && messages === undefined}
            emptyState={
              <div className="flex flex-col items-center justify-center h-72">
                <ChatBubbleLeftRightIcon className="h-10 w-10 text-gray-300 dark:text-gray-600" />
                <p className="mt-3 mb-4 text-sm text-gray-400 dark:text-gray-500 text-center">
                  {fromDay || type !== "all" ? "No messages match these filters" : "No messages yet"}
                </p>
                <Button onClick={() => openComposer()}>New message</Button>
              </div>
            }
          />
        </div>
      </main>
      <DeleteConfirm
        isOpen={!!deleteItem}
        toggleOpen={() => setDeleteItem(null)}
        selectedDeleteItem={deleteItem}
        handleDeleteSuccess={fetchMessages}
        onDelete={deleteMessage}
      />
    </div>
  );
}

export { Messaging };
